import { HttpClientModule } from '@angular/common/http';
import { Injectable, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';

import { filter, map } from 'rxjs/operators';

import { RtAnalyticsModule } from '@recruitee/analytics';
import { AccountBase, DetachedRouterModule, RtcErrorHandlerModule } from '@recruitee/common';
import { I18nModule, LANGUAGE_CONFIG, LOCALE } from '@recruitee/i18n';
import { RtUIModule } from '@recruitee/user-interface';

import { environment } from '../environments/environment';
import { RtoAppComponent } from './app.component';
import { AppRevisionProvider, AppRevisionToken } from './app.revision';
import { RtoAppRoutingModule } from './onboarding.routing.module';
import { OnboardingSharedModule } from './shared/shared.module';

export const defaultLocale = 'en';

@Injectable()
export class Account extends AccountBase {}

@NgModule({
  declarations: [RtoAppComponent],
  imports: [
    RtAnalyticsModule.forRoot({
      disable: !environment.analytics,
      segmentKey: environment.analyticsKey,
      trackRouteChange: false,
    }),
    OnboardingSharedModule,
    BrowserModule,
    I18nModule.forPhraseApp({
      phraseAppProjectId: environment.phraseAppProjectId,
      editor: true,
    }),
    RtcErrorHandlerModule.forRoot(
      {
        enable: environment.production,
        throwOnErrors: !environment.production,
        dsn: environment.sentry_dsn,
        revisionToken: AppRevisionToken,
      },
      Account,
    ),
    RtUIModule.forRoot(),
    RtoAppRoutingModule,
    DetachedRouterModule,
    HttpClientModule,
  ],
  providers: [
    Account,
    AppRevisionProvider,
    { provide: LOCALE, useValue: defaultLocale },
    {
      provide: LANGUAGE_CONFIG,
      useValue: {
        defaultLanguage: 'en',
        languages: ['en', 'nl', 'de', 'fr'],
      },
    },
  ],
  bootstrap: [RtoAppComponent],
})
export class RtoAppModule {
  constructor(private router: Router) {
    if (environment.analytics) {
      router.events
        .pipe(
          filter(evt => evt instanceof NavigationEnd),
          map((evt: NavigationEnd) => {
            const { urlAfterRedirects } = evt;
            if (urlAfterRedirects.endsWith('404')) {
              return { url: urlAfterRedirects, title: '404' };
            } else {
              const urlSegments = urlAfterRedirects
                .split('/')
                .map((v, i) => (i === 2 ? 'user-token' : v));

              const [_, lang, _token, flow, step] = urlSegments;

              return {
                url: urlSegments.join('/'),
                title: `${flow} - ${step}`,
                flow,
                step,
                lang,
              };
            }
          }),
        )
        .subscribe(
          (props: { url: string; title: string; flow?: string; step?: string; lang?: string }) =>
            window.analytics.page(props),
        );
    }
  }
}
